/**
 * Failed Operations Queue for Web App
 * Uses IndexedDB for persistent storage across browser sessions
 * Automatically retries failed Supabase operations
 */

import { openDB, DBSchema, IDBPDatabase } from 'idb'

// Operation types
export enum OperationType {
  ADD_ENTRY = 'add_entry',
  UPDATE_ENTRY = 'update_entry',
  DELETE_ENTRY = 'delete_entry',
  ADD_COLLECTION = 'add_collection',
  UPDATE_COLLECTION = 'update_collection',
  DELETE_COLLECTION = 'delete_collection',
  ADD_TO_COLLECTION = 'add_to_collection',
  REMOVE_FROM_COLLECTION = 'remove_from_collection',
}

// Failed operation interface
export interface FailedOperation {
  id: string
  type: OperationType
  payload: any // JSON serializable data
  timestamp: number
  retryCount: number
  lastRetryDate?: number
  error?: string
}

// IndexedDB schema
interface FailedOperationsDB extends DBSchema {
  operations: {
    key: string
    value: FailedOperation
    indexes: { 'by-timestamp': number }
  }
}

const DB_NAME = 'linkbox-failed-operations'
const DB_VERSION = 1
const STORE_NAME = 'operations'
const MAX_RETRIES = 5

class FailedOperationsQueue {
  private db: IDBPDatabase<FailedOperationsDB> | null = null
  private isProcessing = false

  /**
   * Initialize the IndexedDB database
   */
  async init(): Promise<void> {
    if (this.db) return

    try {
      this.db = await openDB<FailedOperationsDB>(DB_NAME, DB_VERSION, {
        upgrade(db) {
          if (!db.objectStoreNames.contains(STORE_NAME)) {
            const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' })
            store.createIndex('by-timestamp', 'timestamp')
          }
        },
      })
      console.log('✅ [FailedOperationsQueue] IndexedDB initialized')
    } catch (error) {
      // Gracefully degrade — iOS WebKit/private browsing may block IndexedDB
      console.warn('⚠️ [FailedOperationsQueue] IndexedDB unavailable, queue disabled:', error)
      this.db = null
    }
  }

  /**
   * Queue a failed operation for later retry
   */
  async queueFailedOperation(
    type: OperationType,
    payload: any,
    error: Error
  ): Promise<void> {
    await this.init()
    if (!this.db) throw new Error('Database not initialized')

    const operation: FailedOperation = {
      id: crypto.randomUUID(),
      type,
      payload,
      timestamp: Date.now(),
      retryCount: 0,
      error: error.message,
    }

    try {
      await this.db.add(STORE_NAME, operation)
      console.log('✅ [FailedOperationsQueue] Queued operation:', {
        id: operation.id,
        type: operation.type,
        error: error.message,
      })
      console.log('📝 Operation will be retried when user opens app again')
      console.log('   Even if browser closes, this will be retried on next visit')
    } catch (error) {
      console.error('❌ [FailedOperationsQueue] CRITICAL: Failed to queue operation!', error)
      console.error('   This operation will NOT be retried!')
    }
  }

  /**
   * Get all queued operations
   */
  async getQueuedOperations(): Promise<FailedOperation[]> {
    await this.init()
    if (!this.db) return []

    try {
      const operations = await this.db.getAll(STORE_NAME)
      return operations.sort((a: any, b: any) => a.timestamp - b.timestamp)
    } catch (error) {
      console.error('❌ [FailedOperationsQueue] Failed to fetch operations:', error)
      return []
    }
  }

  /**
   * Get count of queued operations
   */
  async getQueuedOperationsCount(): Promise<number> {
    await this.init()
    if (!this.db) return 0

    try {
      return await this.db.count(STORE_NAME)
    } catch (error) {
      console.error('❌ [FailedOperationsQueue] Failed to count operations:', error)
      return 0
    }
  }

  /**
   * Process all queued operations
   */
  async processQueuedOperations(): Promise<void> {
    if (this.isProcessing) {
      console.log('⏭️ Already processing queue, skipping')
      return
    }

    this.isProcessing = true

    try {
      console.log('\n' + '='.repeat(60))
      console.log('🔄 PROCESSING FAILED OPERATIONS QUEUE')
      console.log('='.repeat(60))

      const operations = await this.getQueuedOperations()
      console.log(`📊 Found ${operations.length} queued operation(s)`)

      for (const operation of operations) {
        await this.processOperation(operation)
      }

      console.log('✅ Finished processing queue')
      console.log('='.repeat(60) + '\n')
    } finally {
      this.isProcessing = false
    }
  }

  /**
   * Process a single operation
   */
  private async processOperation(operation: FailedOperation): Promise<void> {
    console.log('\n📦 Processing operation:')
    console.log('   ID:', operation.id)
    console.log('   Type:', operation.type)
    console.log('   Retry count:', operation.retryCount)
    console.log('   Original error:', operation.error || 'unknown')

    // Check retry limit
    if (operation.retryCount >= MAX_RETRIES) {
      console.log(`⚠️ Operation exceeded max retries (${MAX_RETRIES}), giving up`)
      await this.deleteOperation(operation.id)
      return
    }

    // Try to process based on type
    try {
      await this.retryOperation(operation)

      // Success! Remove from queue
      console.log('✅ Operation succeeded! Removing from queue')
      await this.deleteOperation(operation.id)
    } catch (error: any) {
      // Failed again, increment retry count
      console.log('❌ Operation failed again:', error.message)
      console.log(`   Will retry later (attempt ${operation.retryCount + 1}/${MAX_RETRIES})`)

      await this.updateOperationRetryCount(
        operation.id,
        operation.retryCount + 1,
        error.message
      )
    }
  }

  /**
   * Retry an operation based on its type
   */
  private async retryOperation(operation: FailedOperation): Promise<void> {
    const { type, payload } = operation

    // Import Supabase client dynamically to avoid circular dependencies
    const { createClient } = await import('@/lib/supabase/client')
    const supabase = createClient()

    switch (type) {
      case OperationType.ADD_ENTRY:
        await supabase.from('entries').insert(payload).throwOnError()
        console.log('✅ Successfully added entry to Supabase')
        break

      case OperationType.UPDATE_ENTRY:
        await supabase
          .from('entries')
          .update(payload)
          .eq('id', payload.id)
          .throwOnError()
        console.log('✅ Successfully updated entry in Supabase')
        break

      case OperationType.DELETE_ENTRY:
        await supabase
          .from('entries')
          .delete()
          .eq('id', payload.id)
          .throwOnError()
        console.log('✅ Successfully deleted entry from Supabase')
        break

      case OperationType.ADD_COLLECTION:
        await supabase.from('collections').insert(payload).throwOnError()
        console.log('✅ Successfully added collection to Supabase')
        break

      case OperationType.UPDATE_COLLECTION:
        await supabase
          .from('collections')
          .update(payload)
          .eq('id', payload.id)
          .throwOnError()
        console.log('✅ Successfully updated collection in Supabase')
        break

      case OperationType.DELETE_COLLECTION:
        await supabase
          .from('collections')
          .delete()
          .eq('id', payload.id)
          .throwOnError()
        console.log('✅ Successfully deleted collection from Supabase')
        break

      case OperationType.ADD_TO_COLLECTION:
        await supabase
          .from('collection_entries')
          .insert({
            collection_id: payload.collection_id,
            entry_id: payload.entry_id,
          })
          .throwOnError()
        console.log('✅ Successfully added entry to collection in Supabase')
        break

      case OperationType.REMOVE_FROM_COLLECTION:
        await supabase
          .from('collection_entries')
          .delete()
          .eq('collection_id', payload.collection_id)
          .eq('entry_id', payload.entry_id)
          .throwOnError()
        console.log('✅ Successfully removed entry from collection in Supabase')
        break

      default:
        console.warn('⚠️ Unknown operation type:', type)
        throw new Error(`Unknown operation type: ${type}`)
    }
  }

  /**
   * Delete an operation from the queue
   */
  private async deleteOperation(id: string): Promise<void> {
    await this.init()
    if (!this.db) return

    try {
      await this.db.delete(STORE_NAME, id)
      console.log('🗑️ Deleted operation:', id)
    } catch (error) {
      console.error('❌ Failed to delete operation:', error)
    }
  }

  /**
   * Update operation retry count
   */
  private async updateOperationRetryCount(
    id: string,
    newCount: number,
    error?: string
  ): Promise<void> {
    await this.init()
    if (!this.db) return

    try {
      const operation = await this.db.get(STORE_NAME, id)
      if (operation) {
        operation.retryCount = newCount
        operation.lastRetryDate = Date.now()
        if (error) {
          operation.error = error
        }
        await this.db.put(STORE_NAME, operation)
      }
    } catch (error) {
      console.error('❌ Failed to update operation:', error)
    }
  }

  /**
   * Clear all operations (use with caution)
   */
  async clearAllOperations(): Promise<void> {
    await this.init()
    if (!this.db) return

    try {
      await this.db.clear(STORE_NAME)
      console.log('🗑️ Cleared all queued operations')
    } catch (error) {
      console.error('❌ Failed to clear operations:', error)
    }
  }
}

// Singleton instance
export const failedOperationsQueue = new FailedOperationsQueue()

// Initialize on module load
if (typeof window !== 'undefined') {
  failedOperationsQueue.init().catch(console.error)
}
